import Person              from '@/domains/Persons/Person';
import Partner             from '@/domains/Partners/Partner';
import TimeStamp           from '@/core/types/TimeStamp';
import EntityPointer       from '@/domains/EntityPointer/EntityPointer';
import ChangelogEntityDiff from '@/core/services/Changelogs/ChangelogEntityDiff';
import ChangelogsService   from '@/core/services/Changelogs/ChangelogsService';
import Core from "@/core/Core";

/**
 * Запись лога изменений — зеркало ChangelogEntry.php.
 *
 * Иммутабельна, не наследует BaseEntity: у лога нет version/commentsStatus/CRUD.
 * Несёт данные + read-логику. Вложенное гидрируется в модели
 * (Person/Partner/EntityPointer/ChangelogEntityDiff) — единообразно с FeedRecord,
 * без сырых объектов.
 *
 * Текстовое представление события (кто что на что изменил) собирает сервис
 * через геттеры — логика парсинга общая для реестра и фида, поэтому живёт
 * в ChangelogsService, а не дублируется в модели.
 */
class ChangelogEntry {

	id: number;
	eventTime: TimeStamp;
	author: Person | null = null;
	partner: Partner | null = null;

	/**
	 * Имя партнёра, зафиксированное в записи (снепшот на момент события).
	 * Реальное имя (приватная запись / запись покупателя) либо строка-маска
	 * AnimalMask (публичная копия продавца). Системное событие — null.
	 */
	partnerName: string | null = null;

	eventTypeName: string;

	/** Сущность, к которой относится событие (typeName + id + name + version). */
	parentEntity: EntityPointer;

	changes: ChangelogEntityDiff[] = [];

	reasonText: string | null = null;

	/** Произвольные данные для красивого отображения записи. */
	data: any = null;

	ip: string | null = null;
	userAgent: string | null = null;

	static fromServer(data: any, result = new ChangelogEntry()): ChangelogEntry {
		const entry = result;

		entry.id            = data.id;
		entry.eventTime     = TimeStamp.fromServer(data.eventTime ?? data.time);
		entry.eventTypeName = data.eventTypeName;
		entry.reasonText    = data.reasonText ?? null;
		entry.data          = data.data ?? null;
		entry.ip            = data.ip ?? null;
		entry.userAgent     = data.userAgent ?? null;
		entry.partnerName   = data.partnerName ?? null;

		if (data.author) {
			entry.author = Person.fromServer(data.author);
		}

		if (data.partner) {
			entry.partner = Partner.fromServer(data.partner);
		}

		if (data.parentEntity) {
			entry.parentEntity = EntityPointer.fromServer(data.parentEntity);
		}

    entry.changes = Core.utils.hydrateArray(data.changes, ChangelogEntityDiff.fromServer);

		return entry;
	}

	/** Человекочитаемое имя типа сущности (из @Title_ru_RU_Class через реестр). */
	get entityTypeName(): string | null {
		return ChangelogsService.resolveEntityTypeName(this.parentEntity.typeName);
	}

	/** Глагол действия: «создал(а)» / «обновил(а)» / «удалил(а)». */
	get eventActionName(): string | undefined {
		return ChangelogsService.EVENT_TYPE_ACTION_LABELS[this.eventTypeName];
	}

	/** Изменения в виде готовых строк для отображения. */
	get changeRecords(): string[] {
		return ChangelogsService.resolveChangeRecords(this.parentEntity.typeName, this.changes);
	}
}

export default ChangelogEntry;
